import {
  API_PATHS,
  type TelegramLinkOffer,
  type TelegramState,
  telegramLinkSchema,
  telegramStateSchema,
} from '@alphadesk/web/contract.ts';
import { useEffect, useState } from 'react';
import { act, actFor, type Outcome, type Refused } from './act.ts';
import { useRead } from './reads.ts';

/**
 * The socket only restates the link after a change, so it is also read on open, and on `refresh`;
 * whichever landed last stands. A failed read leaves it unknown (null), never "not linked".
 */
export function useTelegramState(framed: TelegramState | null): {
  state: TelegramState | null;
  refresh: () => void;
} {
  const { state: read, refresh } = useRead(API_PATHS.telegram, telegramStateSchema);
  const [latest, setLatest] = useState<TelegramState | null>(null);
  const answered = read.status === 'ok' ? read.data : null;

  useEffect(() => {
    if (answered) setLatest(answered);
  }, [answered]);

  useEffect(() => {
    if (framed) setLatest(framed);
  }, [framed]);

  return { state: latest, refresh };
}

// Never minted on panel open: a code is a secret, minted only when asked to link.
export const mintTelegramLink = (): Promise<
  { ok: true; said: string; value: TelegramLinkOffer } | Refused
> => actFor({ do: 'link-telegram' }, telegramLinkSchema);

export const sendTelegramTest = (): Promise<Outcome> => act(API_PATHS.act, { do: 'test-telegram' });

export const unlinkTelegram = (): Promise<Outcome> => act(API_PATHS.act, { do: 'unlink-telegram' });

export const chatName = (chat: { username: string | null; name: string | null }): string =>
  chat.username ? `@${chat.username}` : (chat.name ?? 'that Telegram account');
